import { Dispatch, createContext, useContext, useReducer } from "react";
import { noop } from "lodash";

import type { InitChatbot } from "server/services/chatbots/v0";

type State =
  | { status: "pending"; initChatbotResponse: undefined; error: undefined }
  | { status: "resolved"; initChatbotResponse: InitChatbot; error: undefined }
  | { status: "rejected"; initChatbotResponse: undefined; error: Error };

type Action =
  | { type: "pending" }
  | { type: "resolved"; initChatbotResponse: InitChatbot }
  | { type: "rejected"; error: Error };

const defaultState: State = {
  status: "pending",
  initChatbotResponse: undefined,
  error: undefined,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "pending":
      return {
        status: "pending",
        initChatbotResponse: undefined,
        error: undefined,
      };
    case "resolved":
      return {
        status: "resolved",
        initChatbotResponse: action.initChatbotResponse,
        error: undefined,
      };
    case "rejected":
      return {
        status: "rejected",
        initChatbotResponse: undefined,
        error: action.error,
      };
    default:
      return state;
  }
}

const ChatbotContext = createContext<State>(defaultState);
const SetChatbotContext = createContext<Dispatch<Action>>(noop);

export function ChatbotProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, defaultState);
  return (
    <SetChatbotContext.Provider value={dispatch}>
      <ChatbotContext.Provider value={state}>
        {children}
      </ChatbotContext.Provider>
    </SetChatbotContext.Provider>
  );
}

export function useChatbot() {
  return useContext(ChatbotContext);
}

export function useSetChatbot() {
  return useContext(SetChatbotContext);
}
